import {
  Col,
  Row,
  Space,
  Table,
  Card,
  Spin,
  message,
  Divider,
  Button,
  Typography,
  DatePicker,
  Dropdown,
  Checkbox,
  Input,
} from "antd";
import React, { useCallback, useMemo, useState } from "react";
import {
  PrinterOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  DownOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import "jspdf-autotable";
import XLSX from "xlsx-js-style";
import {
  HEADERS_EXPORT_BEN,
  HEADERS_EXPORT_DETAIL,
  POST_LOAN_DB2_V2,
  POST_REPORT_SELLING,
} from "../../API/apiUrls";
import CurrencyFormat from "../../../hook/CurrencyFormat";
import "../../../assets/font/THSarabunNew-normal";
import "../../../assets/font/THSarabunNew-bold";
import MotionHoc from "../../../utils/MotionHoc";
import TokenCheck from "../../../hook/TokenCheck";
import ThaiDatePicker from "../../../hook/ThaiDatePicker";

const { Title, Text } = Typography;

const TwoLineHeader = ({ top, bottom }) => (
  <div style={{ textAlign: "center", lineHeight: 1.4 }}>
    <div>{top}</div>
    <div style={{ fontSize: 11, fontWeight: 400, color: "#888" }}>{bottom}</div>
  </div>
);

const Main = () => {
  const [
    currencyFormat,
    currencyFormatComma,
    currencyFormatPoint,
    currencyFormatNoPoint,
  ] = CurrencyFormat();
  const [signOut] = TokenCheck();
  const [loading, setLoading] = useState(false);
  const [arrayPay, setArrayPay] = useState([]);
  const [startDate, setStartDate] = useState(dayjs().startOf("month"));
  const [endDate, setEndDate] = useState(dayjs().endOf("month"));
  const [isFlagAgreement, setIsFlagAgreement] = useState(true);
  const [isFlagEnforce, setIsFlagEnforce] = useState(true);
  const [searchContno, setSearchContno] = useState("");

  // ── Query ─────────────────────────────────────────────
  const onQuery = () => {
    if (!startDate || !endDate) {
      message.warning("กรุณาเลือกวันที่ก่อนค้นหา");
      return;
    }
    queryData({
      firstDate: dayjs(startDate).format("YYYY-MM-DDTHH:mm:ssZ"),
      lastDate: dayjs(endDate).format("YYYY-MM-DDTHH:mm:ssZ"),
    });
  };

  const queryData = async (payload) => {
    setLoading(true);
    try {
      const res = await axios.post(POST_REPORT_SELLING, payload, {
        headers: HEADERS_EXPORT_DETAIL,
      });
      if (res.status !== 200) {
        setArrayPay([]);
        return;
      }

      const reportData = res.data?.data ?? [];

      if (!reportData.length) {
        setArrayPay([]);
        return;
      }
      console.log("reportData", reportData);

      const groupedBySchema = reportData.reduce((acc, item) => {
        if (!item.CONTNO || !item.SCHEMA) return acc;
        if (!acc[item.SCHEMA]) acc[item.SCHEMA] = [];
        acc[item.SCHEMA].push(`'${item.CONTNO}'`);
        return acc;
      }, {});

      const schemaPayload = Object.entries(groupedBySchema).reduce(
        (acc, [schema, arr]) => {
          acc[schema] = arr.join(",");
          return acc;
        },
        {},
      );

      let db2Map = {};
      try {
        const db2Res = await axios.post(POST_LOAN_DB2_V2, schemaPayload, {
          headers: HEADERS_EXPORT_BEN,
        });
        const db2Data = Array.isArray(db2Res.data)
          ? db2Res.data
          : Array.isArray(db2Res.data?.data)
            ? db2Res.data.data
            : [];
        console.log("db2Data", db2Data);

        db2Data.forEach((item) => {
          const contno = item?.LOAN?.CONTNO;
          if (!contno) return;
          db2Map[contno] = {
            LOCAT: item.LOAN?.LOCAT,
            TOTPRC: item.LOAN?.TOTPRC,
            NCSHPRC: item.LOAN?.NCSHPRC,
            SMPAY: item.LOAN?.SMPAY,
            PRODUCTLOAN: item.LOAN?.PRODUCTLOAN,
            ISSUNO: item.LOAN?.ISSUNO,
            TYPE: item.MORTGAGE?.TYPE,
            MODEL: item.MORTGAGE?.MODEL,
            BAAB: item.MORTGAGE?.BAAB,
            COLOR: item.MORTGAGE?.COLOR,
            REGNO: item.MORTGAGE?.REGNO,
            ENGNO: item.MORTGAGE?.ENGNO,
            MANUYR: item.MORTGAGE?.MANUYR,
            DORECV: item.MORTGAGE?.DORECV,
            OFFIC: item.CUSTOMER?.OFFIC,
            OCCUP: item.CUSTOMER?.OCCUP,
            BIRTHDT: item.CUSTOMER?.BIRTHDT,
            ADDRESS: item.CUSTOMER?.ADDRESS ?? [],
            GUARANTORS: item.GUARANTORS ?? [],
          };
        });
      } catch (e) {
        console.warn("DB2 failed:", e);
      }
      setArrayPay(
        reportData
          .map((item) => ({ ...item, ...(db2Map[item.CONTNO] ?? {}) }))
          .sort((a, b) => dayjs(a.SDATE).valueOf() - dayjs(b.SDATE).valueOf()),
      );
    } catch (err) {
      message.error(
        err?.response?.status === 404 ? "ไม่พบข้อมูล" : "เกิดข้อผิดพลาด",
      );
      if (err?.response?.status === 401) {
        signOut();
      }
      setArrayPay([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterData = useCallback(() => {
    if (!arrayPay.length) return [];

    const keyword = searchContno.trim().toLowerCase();

    return arrayPay.filter((item) => {
      // ✅ ตัด closeContno = 1 ออกเสมอ
      // if (item.closeContno === 1) return false;

      // ✅ ค้นหาจากเลขสัญญา (เดิม/ใหม่)
      if (keyword) {
        const contno = (item.CONTNO || "").toLowerCase();
        const newContno = (item.newContno || "").toLowerCase();
        if (!contno.includes(keyword) && !newContno.includes(keyword)) {
          return false;
        }
      }

      // ✅ ทั้งคู่ true → เอาทั้งหมด
      if (isFlagAgreement && isFlagEnforce) return true;

      // ✅ Agreement อย่างเดียว → flagLoanNew = 1
      if (isFlagAgreement && !isFlagEnforce) return item.flagLoanNew === 1;

      // ✅ Enforce อย่างเดียว → flagLoanNew = 2
      if (!isFlagAgreement && isFlagEnforce) return item.flagLoanNew === 2;

      // ✅ ทั้งคู่ false → ไม่มีข้อมูล
      return false;
    });
  }, [arrayPay, isFlagAgreement, isFlagEnforce, searchContno]);

  // ── Columns ───────────────────────────────────────────
  const columns = [
    {
      title: "ลำดับ",
      key: "index",
      align: "center",
      width: 60,
      render: (_, __, i) => i + 1,
    },
    {
      title: "สาขา",
      align: "center",
      width: 60,
      render: (_, r) => "MIT",
    },
    {
      title: <TwoLineHeader top="เลขสัญญา" bottom="(สถานะ)" />,
      key: "contract_model",
      align: "center",
      width: 130,
      render: (_, r) => (
        <>
          <div style={{ fontWeight: 600 }}>{r.newContno || "-"}</div>
          <div style={{ color: "#888", fontSize: 12 }}>
            {`(${
              r.flagLoanNew === 1 && r.LAW_TYPE_ID !== 6
                ? "ทำยอม"
                : r.flagLoanNew === 1 && r.LAW_TYPE_ID === 6
                  ? "ทำยอม(ฟ้องส่วนต่าง)"
                  : r.flagLoanNew === 2 && r.LAW_TYPE_ID === 6
                    ? "พิพากษา(ฟ้องส่วนต่าง)"
                    : "พิพากษา"
            })`.trim()}
          </div>
        </>
      ),
    },
    {
      title: "คำฟ้อง",
      key: "lawsuit_info",
      align: "left",
      width: 160,
      render: (_, r) => (
        <div style={{ fontSize: 13, lineHeight: 1.8 }}>
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              เลขคดีดำ :{" "}
            </Text>
            <Text style={{ fontFamily: "monospace" }}>
              {r.blackCaseNumber || "-"}
            </Text>
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              วันประทับฟ้อง :{" "}
            </Text>
            <Text>
              {r.dateOfPlaint
                ? dayjs(r.dateOfPlaint).add(543, "year").format("DD/MM/YYYY")
                : "-"}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: "ชั้นศาล",
      key: "case_info",
      align: "left",
      width: 160,
      render: (_, r) => (
        <div style={{ fontSize: 13, lineHeight: 1.8 }}>
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              เลขคดีแดง :{" "}
            </Text>
            <Text style={{ fontFamily: "monospace" }}>
              {r.redCaseNumber || "-"}
            </Text>
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              วันพิพากษา :{" "}
            </Text>
            <Text>
              {r.judgeDate
                ? dayjs(r.judgeDate).add(543, "year").format("DD/MM/YYYY")
                : "-"}
            </Text>
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              วันวางคำบังคับ :{" "}
            </Text>
            <Text>
              {r.enforceCaseDate
                ? dayjs(r.enforceCaseDate).add(543, "year").format("DD/MM/YYYY")
                : "-"}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: "ชื่อลูกค้า",
      key: "name_strno",
      align: "left",
      width: 140,
      render: (_, r) => (
        <div>
          {`${r.SNAM || ""}${r.NAME1 || ""} ${r.NAME2 || ""}`.trim() || "-"}
        </div>
      ),
    },
    {
      title: "เลขตัวถัง",
      key: "name_strno",
      align: "left",
      width: 140,
      render: (_, r) => <div>{r.STRNO || "-"}</div>,
    },
    {
      title: <TwoLineHeader top="วันทำสัญญา" bottom="งวดแรก" />,
      align: "center",
      width: 90,
      sorter: (a, b) => dayjs(a.SDATE).valueOf() - dayjs(b.SDATE).valueOf(),
      render: (_, r) => (
        <>
          <div>
            {r.SDATE
              ? dayjs(r.SDATE).add(543, "year").format("DD/MM/YYYY")
              : "-"}
          </div>
          <div style={{ color: "#888", fontSize: 12 }}>
            {dayjs(r.FDATE).add(543, "year").format("DD/MM/YYYY")}
          </div>
        </>
      ),
    },
    {
      title: "จำนวนงวด",
      dataIndex: "T_NOPAY",
      key: "T_NOPAY",
      align: "center",
      width: 70,
      sorter: (a, b) => {
        if (a.T_NOPAY == null && b.T_NOPAY == null) return 0;
        if (a.T_NOPAY == null) return 1; // a ไปท้าย
        if (b.T_NOPAY == null) return -1; // b ไปท้าย
        return a.T_NOPAY - b.T_NOPAY;
      },
      render: (val) => <div>{val ?? "1"}</div>,
    },
    {
      title: "ค่างวด",
      key: "upay_vat",
      align: "right",
      width: 100,
      sorter: (a, b) => {
        const valA =
          a.flagLoanNew === 1 ? (a.TOT_UPAY ?? 0) : (a.creditDebtor ?? 0);
        const valB =
          b.flagLoanNew === 1 ? (b.TOT_UPAY ?? 0) : (b.creditDebtor ?? 0);
        return valA - valB;
      },
      render: (_, r) => (
        <div>
          {r.flagLoanNew === 1
            ? currencyFormatPoint(r.TOT_UPAY)
            : currencyFormatPoint(r.creditDebtor)}
        </div>
      ),
    },
    {
      title: "ยอดศาลสั่งจ่าย",
      key: "totalJugde",
      align: "right",
      width: 90,
      sorter: (a, b) => (a.totalJugde ?? 0) - (b.totalJugde ?? 0),
      render: (_, r) => <div>{currencyFormatPoint(r.totalJugde)}</div>,
    },
    {
      title: "ต้นทุนเก่า",
      key: "credit_debtor",
      align: "right",
      width: 110,
      sorter: (a, b) => (a.creditDebtorOld ?? 0) - (b.creditDebtorOld ?? 0),
      render: (_, r) => <div>{currencyFormatPoint(r.creditDebtorOld)}</div>,
    },
    {
      title: <TwoLineHeader top="ราคาซื้อ" bottom="เปอร์เซ็นต์" />,
      key: "credit_debtor",
      align: "right",
      width: 110,
      sorter: (a, b) =>
        dayjs(a.creditDebtor).valueOf() - dayjs(b.creditDebtor).valueOf(),
      render: (_, r) => (
        <>
          <div>{currencyFormatPoint(r.creditDebtor)}</div>
          <div style={{ color: "#130f0f", fontSize: 12 }}>
            {r.creditPercen}%
          </div>
        </>
      ),
    },
    {
      title: "ดอกผล",
      key: "interest",
      align: "right",
      width: 90,
      sorter: (a, b) => {
        const valA =
          a.flagLoanNew === 1
            ? (parseFloat(a.totalJugde) || 0) -
              (parseFloat(a.creditDebtor) || 0)
            : 0;
        const valB =
          b.flagLoanNew === 1
            ? (parseFloat(b.totalJugde) || 0) -
              (parseFloat(b.creditDebtor) || 0)
            : 0;
        return valA - valB;
      },
      render: (_, r) => (
        <div>
          {r.flagLoanNew === 1
            ? currencyFormatPoint(
                (parseFloat(r.totalJugde) || 0) -
                  (parseFloat(r.creditDebtor) || 0),
              )
            : "-"}
        </div>
      ),
    },
  ];

  // ── Export Excel ──────────────────────────────────────
  const handleExportExcel = () => {
    if (!filteredData.length) {
      message.warning("ไม่มีข้อมูล");
      return;
    }

    const fromDate = startDate ? dayjs(startDate).format("DD/MM/YYYY") : "";
    const toDate = endDate ? dayjs(endDate).format("DD/MM/YYYY") : "";

    const rows = filteredData.map((r, i) => ({
      ลำดับ: i + 1,
      สาขา: "MIT",
      เลขสัญญา: r.CONTNO || "",
      เลขสัญญาใหม่: r.newContno || "",
      สถานะ:
        r.flagLoanNew === 1 && r.LAW_TYPE_ID !== 6
          ? "ทำยอม"
          : r.flagLoanNew === 1 && r.LAW_TYPE_ID === 6
            ? "ทำยอม(ฟ้องส่วนต่าง)"
            : r.flagLoanNew === 2 && r.LAW_TYPE_ID === 6
              ? "พิพากษา(ฟ้องส่วนต่าง)"
              : "พิพากษา",
      เลขคดีดำ: r.blackCaseNumber || "",
      วันประทับฟ้อง: r.dateOfPlaint
        ? dayjs(r.dateOfPlaint).add(543, "year").format("DD/MM/YYYY")
        : "",
      เลขคดีแดง: r.redCaseNumber || "",
      วันพิพากษา: r.judgeDate
        ? dayjs(r.judgeDate).add(543, "year").format("DD/MM/YYYY")
        : "",
      วันวางคำบังคับ: r.enforceCaseDate
        ? dayjs(r.enforceCaseDate).add(543, "year").format("DD/MM/YYYY")
        : "",
      ชื่อลูกค้า: `${r.SNAM || ""}${r.NAME1 || ""} ${r.NAME2 || ""}`.trim(),
      เลขบัตร: r.CUSCOD || "",
      เลขตัวถัง: r.STRNO || "",
      ยี่ห้อ: r.TYPE || "",
      รุ่น: r.MODEL || "",
      แบบ: r.BAAB || "",
      สี: r.COLOR || "",
      ทะเบียน: r.REGNO || "",
      เลขเครื่อง: r.ENGNO || "",
      ปีผลิต: r.MANUYR || "",
      วันทำสัญญา: r.SDATE
        ? dayjs(r.SDATE).add(543, "year").format("DD/MM/YYYY")
        : "",
      งวดแรก:
        r.flagLoanNew === 1
          ? dayjs(r.FDATE).add(543, "year").format("DD/MM/YYYY")
          : dayjs(r.SDATE).add(543, "year").format("DD/MM/YYYY"),
      งวดสุดท้าย:
        r.flagLoanNew === 1
          ? dayjs(r.LDATE).add(543, "year").format("DD/MM/YYYY")
          : "",
      จำนวนงวด: r.T_NOPAY ?? "1",
      ค่างวด:
        r.flagLoanNew === 1
          ? parseFloat(r.TOT_UPAY)
          : parseFloat(r.creditDebtor),
      ยอดที่ศาลสั่ง: parseFloat(r.totalJugde) || 0,
      ต้นทุนเก่า: parseFloat(r.creditDebtorOld) || 0,
      ราคาซื้อ: parseFloat(r.creditDebtor) || 0,
      เปอร์เซ็นยอด: parseFloat(r.creditPercen) || 0,
      ดอกผลเช่าซื้อ:
        r.flagLoanNew === 1
          ? (parseFloat(r.totalJugde) || 0) - (parseFloat(r.creditDebtor) || 0)
          : "-",
    }));

    const summaryRow = {
      ลำดับ: "",
      สาขา: `รวมทั้งหมด ${filteredData.length} รายการ`,
    };
    const moneyKeys = [
      "ค่างวด",
      "ยอดที่ศาลสั่ง",
      "ต้นทุนเก่า",
      "ราคาซื้อ",
      "ดอกผลเช่าซื้อ",
    ];
    moneyKeys.forEach((key) => {
      summaryRow[key] = rows.reduce(
        (sum, r) => sum + (parseFloat(r[key]) || 0),
        0,
      );
    });

    const allRows = [...rows, summaryRow];
    const ws = XLSX.utils.json_to_sheet(allRows);

    const keys = Object.keys(rows[0]);

    // ✅ style สลับสีทุก cell
    const evenFill = { patternType: "solid", fgColor: { rgb: "EBF5FF" } }; // น้ำเงินอ่อน
    const oddFill = { patternType: "solid", fgColor: { rgb: "FFFFFF" } }; // ขาว
    const summaryFill = { patternType: "solid", fgColor: { rgb: "D5E8D4" } }; // เขียวอ่อน

    const borderStyle = {
      top: { style: "thin", color: { rgb: "CCCCCC" } },
      bottom: { style: "thin", color: { rgb: "CCCCCC" } },
      left: { style: "thin", color: { rgb: "CCCCCC" } },
      right: { style: "thin", color: { rgb: "CCCCCC" } },
    };

    // header style (แถวที่ 1)
    keys.forEach((_, ci) => {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: ci });
      if (!ws[cellRef]) return;
      ws[cellRef].s = {
        fill: { patternType: "solid", fgColor: { rgb: "344962" } },
        font: { bold: true, color: { rgb: "FFFFFF" }, sz: 12 },
        alignment: { horizontal: "center", vertical: "center" },
        border: borderStyle,
      };
    });

    // data rows
    rows.forEach((row, ri) => {
      const isEven = ri % 2 === 0;
      const fill = isEven ? evenFill : oddFill;
      keys.forEach((key, ci) => {
        const cellRef = XLSX.utils.encode_cell({ r: ri + 1, c: ci });
        if (!ws[cellRef]) ws[cellRef] = { v: "", t: "s" };

        const isNumber = typeof row[key] === "number";
        const isMoney = moneyKeys.includes(key); // ✅
        const isPercent = key === "เปอร์เซ็นเครดิต"; // ✅

        ws[cellRef].s = {
          fill,
          font: { sz: 11 },
          alignment: {
            horizontal: isNumber ? "right" : "left",
            vertical: "center",
          },
          border: borderStyle,
          numFmt: isMoney
            ? "#,##0.00" // ✅ currency
            : isPercent
              ? '0.00"%"' // ✅ percent
              : undefined,
        };
      });
    });

    // summary row
    const summaryRowIdx = rows.length + 1;
    keys.forEach((key, ci) => {
      // ✅ เปลี่ยน _ เป็น key
      const cellRef = XLSX.utils.encode_cell({ r: summaryRowIdx, c: ci });
      if (!ws[cellRef]) return;

      const isMoney = moneyKeys.includes(key); // ✅

      ws[cellRef].s = {
        fill: summaryFill,
        font: { bold: true, sz: 12 },
        alignment: {
          horizontal: isMoney ? "right" : "left", // ✅ แก้จาก ci >= 22
          vertical: "center",
        },
        border: borderStyle,
        numFmt: isMoney ? "#,##0.00" : undefined, // ✅
      };
    });

    ws["!cols"] = keys.map((key) => ({ wch: Math.max(key.length * 2, 14) }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "รายงาน");
    XLSX.writeFile(wb, `รายงานการขายผ่อน_${fromDate}_${toDate}.xlsx`);
    message.success("Export Excel สำเร็จ");
  };

  // ── Export PDF ────────────────────────────────────────
  const handlePrintPDF = () => {
    if (!filteredData.length) {
      message.warning("ไม่มีข้อมูล");
      return;
    }

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });
    const pageWidth = doc.internal.pageSize.getWidth();
    const now = dayjs().format("DD/MM/YYYY HH:mm:ss");
    const fromDate = startDate ? dayjs(startDate).format("DD/MM/YYYY") : "";
    const toDate = endDate ? dayjs(endDate).format("DD/MM/YYYY") : "";

    // ✅ แสดงบริษัทและที่อยู่ทุกหน้า
    const drawHeader = (pageNum) => {
      doc.setFont("THSarabunNew", "bold");
      doc.setFontSize(16);
      doc.text("รายงานการขายผ่อนเพื่อแผนกบัญชี", pageWidth / 2, 8, {
        align: "center",
      });
      doc.text(
        `ขายจากวันที่  ${fromDate}   ถึงวันที่  ${toDate}`,
        pageWidth / 2,
        14,
        { align: "center" },
      );
      doc.setFont("THSarabunNew", "normal");
      doc.setFontSize(14);
      doc.text("บริษัท วัน เอเอ็มซี จำกัด (สำนักงานใหญ่)", 8, 18);
      doc.text(
        "89 หมู่ 14 ถ.มิตรภาพ ต.ในเมือง อ.เมืองขอนแก่น จ.ขอนแก่น 40000",
        8,
        22,
      );

      doc.setFontSize(14);
      doc.text(`วันที่พิมพ์ ${now}`, 8, 28);
      doc.text(`หน้าที่ ${pageNum}`, pageWidth - 8, 28, { align: "right" });
    };

    const statusLabel = (r) =>
      r.flagLoanNew === 1 && r.LAW_TYPE_ID !== 6
        ? "ทำยอม"
        : r.flagLoanNew === 1 && r.LAW_TYPE_ID === 6
          ? "ทำยอม(ฟ้องส่วนต่าง)"
          : r.flagLoanNew === 2 && r.LAW_TYPE_ID === 6
            ? "พิพากษา(ฟ้องส่วนต่าง)"
            : "พิพากษา";

    const tableBody = filteredData.map((r, index) => [
      index + 1,
      `${r.newContno ?? ""}\n(${statusLabel(r)})\nMIT`,
      `เลขคดีดำ : ${r.blackCaseNumber || "-"}\nวันประทับฟ้อง : ${
        r.dateOfPlaint
          ? dayjs(r.dateOfPlaint).add(543, "year").format("DD/MM/YYYY")
          : "-"
      }`,
      `เลขคดีแดง : ${r.redCaseNumber || "-"}\nวันพิพากษา : ${
        r.judgeDate
          ? dayjs(r.judgeDate).add(543, "year").format("DD/MM/YYYY")
          : "-"
      }\nวันวางคำบังคับ : ${
        r.enforceCaseDate
          ? dayjs(r.enforceCaseDate).add(543, "year").format("DD/MM/YYYY")
          : "-"
      }`,
      `${r.SNAM || ""}${r.NAME1 || ""} ${r.NAME2 || ""}`.trim() || "-",
      r.STRNO || "-",
      (r.SDATE ? dayjs(r.SDATE).add(543, "year").format("DD/MM/YYYY") : "-") +
        "\n" +
        dayjs(r.FDATE).add(543, "year").format("DD/MM/YYYY"),
      `${r.T_NOPAY ?? "1"}`,
      r.flagLoanNew === 1
        ? currencyFormatPoint(r.TOT_UPAY)
        : currencyFormatPoint(r.creditDebtor),
      currencyFormatPoint(r.totalJugde),
      currencyFormatPoint(r.creditDebtorOld),
      `${currencyFormatPoint(r.creditDebtor)}\n${r.creditPercen}%`,
      r.flagLoanNew === 1
        ? currencyFormatPoint(
            (parseFloat(r.totalJugde) || 0) - (parseFloat(r.creditDebtor) || 0),
          )
        : "-",
    ]);

    const sumAllRows = (rows) =>
      rows.reduce(
        (acc, r) => ({
          totUpay:
            acc.totUpay +
            (parseFloat(r.TOT_UPAY) || parseFloat(r.creditDebtor)),
          totalJugde: acc.totalJugde + (parseFloat(r.totalJugde) || 0),
          creditDebtorOld:
            acc.creditDebtorOld + (parseFloat(r.creditDebtorOld) || 0), // ✅ เพิ่ม
          creditDebtor: acc.creditDebtor + (parseFloat(r.creditDebtor) || 0),
          interest:
            acc.interest +
            (r.flagLoanNew === 1
              ? (parseFloat(r.totalJugde) || 0) -
                (parseFloat(r.creditDebtor) || 0)
              : 0),
        }),
        {
          totUpay: 0,
          totalJugde: 0,
          creditDebtorOld: 0, // ✅ เพิ่ม
          creditDebtor: 0,
          interest: 0,
        },
      );

    const t = sumAllRows(filteredData);

    const makeSummaryRow = (
      label,
      count,
      s,
      textColor = [0, 0, 0],
      fillColor = [236, 240, 241],
    ) => {
      const c = (v) => ({
        content: v,
        styles: {
          halign: "right",
          fontStyle: "bold",
          font: "THSarabunNew",
          fillColor,
          textColor,
        },
      });
      return [
        {
          content: `${label}  ${count} รายการ`,
          colSpan: 8, // ลำดับ+เลขสัญญา(สถานะ/สาขา)+คำฟ้อง+ชั้นศาล+ชื่อ+เลขตัวถัง+วันที่+จำนวนงวด
          styles: {
            fontStyle: "bold",
            font: "THSarabunNew",
            fillColor,
            textColor,
          },
        },
        c(currencyFormatPoint(s.totUpay)), // col 7: ค่างวด
        c(currencyFormatPoint(s.totalJugde)), // col 8: ยอดที่ศาลสั่ง
        c(currencyFormatPoint(s.creditDebtorOld)), // col 9: ต้นทุนเก่า
        c(currencyFormatPoint(s.creditDebtor)), // col 10: ราคาซื้อ
        c(currencyFormatPoint(s.interest)), // col 11: ดอกผล
      ];
    };

    doc.autoTable({
      startY: 30,
      head: [
        [
          "ลำดับ",
          "เลขสัญญา\n(สถานะ)\nสาขา",
          "คำฟ้อง",
          "ชั้นศาล",
          "ชื่อลูกค้า",
          "เลขตัวถัง",
          "วันทำสัญญา\nงวดแรก",
          "จำนวนงวด",
          "ค่างวด",
          "ยอดที่ศาลสั่ง",
          "ต้นทุนเก่า",
          "ราคาซื้อ\nเปอร์เซ็นต์",
          "ดอกผล",
        ],
      ],
      body: [
        ...tableBody,
        makeSummaryRow("รวมทั้งหมด", filteredData.length, t),
      ],

      headStyles: {
        font: "THSarabunNew",
        fillColor: [52, 73, 94],
        textColor: 255,
        halign: "center",
        fontStyle: "bold",
        fontSize: 14,
      },
      columnStyles: {
        2: { halign: "left" }, // ✅ คำฟ้อง
        3: { halign: "left" }, // ✅ ชั้นศาล
        4: { halign: "left" }, // ✅ ชื่อลูกค้า
        5: { halign: "left" }, // ✅ เลขตัวถัง

        8: { halign: "right" },
        9: { halign: "right" },
        10: { halign: "right" },
        11: { halign: "right" },
        12: { halign: "right" },
      },
      alternateRowStyles: { fillColor: [235, 245, 255] }, // ✅ น้ำเงินอ่อน
      styles: {
        font: "THSarabunNew",
        fontSize: 14,
        cellPadding: 1,
        halign: "center",
        fillColor: [255, 255, 255],
      },
      didParseCell: (data) => {
        if (data.section === "body" && data.row.index < tableBody.length) {
          if (filteredData[data.row.index]?.FLAG === "C") {
            data.cell.styles.textColor = [220, 53, 69];
            data.cell.styles.fontStyle = "bold";
          }
        }
      },
      didDrawPage: () =>
        drawHeader(doc.internal.getCurrentPageInfo().pageNumber),
      margin: { top: 30, left: 3, right: 3 },
    });

    doc.save(`รายงานการขายผ่อน_${fromDate}_${toDate}.pdf`);
  };

  // ✅ Dropdown menu เลือก PDF หรือ Excel
  const exportMenuItems = {
    items: [
      {
        key: "pdf",
        label: "Export PDF",
        icon: <FilePdfOutlined style={{ color: "#c0392b" }} />,
      },
      {
        key: "excel",
        label: "Export Excel",
        icon: <FileExcelOutlined style={{ color: "#27ae60" }} />,
      },
    ],
    onClick: ({ key }) => {
      if (key === "pdf") handlePrintPDF();
      if (key === "excel") handleExportExcel();
    },
  };

  const hasDate = startDate && endDate;
  const filteredData = useMemo(() => handleFilterData(), [handleFilterData]);

  return (
    <>
      <style>{`
      .row-even td { background-color: #f5f8ff !important; }
      .row-odd td  { background-color: #ffffff !important; }
    `}</style>
      <Spin spinning={loading} size="large" tip="Loading...">
        <Row>
          <Col span={16}>
            <Space style={{ marginBottom: 10 }}>
              เริ่ม
              <ThaiDatePicker
                size="large"
                value={startDate}
                onChange={setStartDate}
                placeholder={"วันที่เริ่มต้น"}
                allowClear={false}
              />
              ถึง
              <ThaiDatePicker
                size="large"
                value={endDate}
                onChange={setEndDate}
                placeholder={"วันที่สิ้นสุด"}
                allowClear={false}
              />
              <Button size="large" type="primary" onClick={onQuery}>
                ค้นหา
              </Button>
              <Input
                size="small"
                allowClear
                placeholder="ค้นหาเลขสัญญา"
                value={searchContno}
                onChange={(e) => setSearchContno(e.target.value)}
                style={{ width: 200 }}
              />
              <Checkbox
                checked={isFlagAgreement}
                onChange={(e) => setIsFlagAgreement(e.target.checked)}
              >
                ทำยอม
              </Checkbox>
              <Checkbox
                checked={isFlagEnforce}
                onChange={(e) => setIsFlagEnforce(e.target.checked)}
              >
                พิพากษา
              </Checkbox>
            </Space>
          </Col>
          <Col span={8} style={{ textAlign: "end" }}>
            <Space style={{ marginBottom: 10 }}>
              {/* ✅ Dropdown เลือก PDF / Excel */}
              <Dropdown menu={exportMenuItems} disabled={!filteredData.length}>
                <Button size="large" icon={<PrinterOutlined />}>
                  พิมพ์ / Export <DownOutlined />
                </Button>
              </Dropdown>
            </Space>
          </Col>
        </Row>

        <Card style={{ marginBottom: 10 }}>
          <Divider style={{ fontSize: 20 }}>
            รายงานการขายผ่อนเพื่อแผนกบัญชี
          </Divider>

          <Typography.Text
            strong
            style={{ display: "block", textAlign: "center", fontSize: 16 }}
          >
            บริษัท วัน เอเอ็มซี จำกัด (สำนักงานใหญ่)
          </Typography.Text>

          {hasDate && (
            <Typography.Text
              style={{ display: "block", textAlign: "center", fontSize: 16 }}
            >
              ขายจากวันที่ {dayjs(startDate).format("DD/MM/YYYY")} ถึงวันที่{" "}
              {dayjs(endDate).format("DD/MM/YYYY")}
            </Typography.Text>
          )}

          <Table
            style={{ marginTop: 10 }}
            size="small"
            columns={columns}
            dataSource={filteredData}
            rowKey={(r) => r.idJudge ?? r.CONTNO}
            rowClassName={(_, index) =>
              index % 2 === 0 ? "row-even" : "row-odd"
            }
            scroll={{ x: 1400 }}
            pagination={{
              defaultPageSize: 50,
              showSizeChanger: true,
              showTotal: (t) => `ทั้งหมด ${t} รายการ`,
              pageSizeOptions: ["10", "20", "50", "100"],
            }}
            summary={() => {
              const sumAllRows = (rows) =>
                rows.reduce(
                  (acc, r) => ({
                    totUpay:
                      acc.totUpay +
                      (parseFloat(r.TOT_UPAY) ||
                        parseFloat(r.creditDebtor) ||
                        0),
                    totalJugde:
                      acc.totalJugde + (parseFloat(r.totalJugde) || 0),
                    creditDebtor:
                      acc.creditDebtor + (parseFloat(r.creditDebtor) || 0),
                    creditDebtorOld:
                      acc.creditDebtorOld +
                      (parseFloat(r.creditDebtorOld) || 0),
                    interest:
                      acc.interest +
                      (r.flagLoanNew === 1
                        ? (parseFloat(r.totalJugde) || 0) -
                          (parseFloat(r.creditDebtor) || 0)
                        : 0),
                  }),
                  {
                    totUpay: 0,
                    totalJugde: 0,
                    creditDebtor: 0,
                    creditDebtorOld: 0,
                    interest: 0,
                  },
                );

              const t = sumAllRows(filteredData); // ✅ เปลี่ยนจาก filteredData

              const cs = { fontWeight: "bold", background: "#ecf0f1" };
              const cr = {
                fontWeight: "bold",
                background: "#ecf0f1",
                textAlign: "right",
              };

              return (
                <Table.Summary.Row style={cs}>
                  <Table.Summary.Cell colSpan={7} align="left" style={cs}>
                    รวมทั้งหมด {filteredData.length} รายการ{" "}
                    {/* ✅ เปลี่ยนจาก filteredData */}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell align="right" style={cr}>
                    {currencyFormatPoint(t.totUpay)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell align="right" style={cr}>
                    {currencyFormatPoint(t.totalJugde)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell align="right" style={cr}>
                    {currencyFormatPoint(t.creditDebtorOld)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell align="right" style={cr}>
                    {currencyFormatPoint(t.creditDebtor)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell align="right" style={cr}>
                    {currencyFormatPoint(t.interest)}
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              );
            }}
          />
        </Card>
      </Spin>
    </>
  );
};

const MainReportSelling = MotionHoc(Main);
export default MainReportSelling;
